import { useState } from 'react';
import { ChevronRight, ChevronDown, Plus, Folder, Tag, Bookmark } from 'lucide-react';

interface TreeFolder {
  id: string;
  name: string;
  displayName: string;
  type: 'folder' | 'label' | 'category';
  unreadCount?: number;
  totalCount?: number;
  children?: TreeFolder[];
  color?: string;
}

interface FolderTreeProps {
  folders: TreeFolder[];
  selectedIds: string[];
  onSelect: (folder: TreeFolder) => void;
}

interface FolderNodeProps {
  folder: TreeFolder;
  level: number;
  isSelected: boolean;
  onSelect: (folder: TreeFolder) => void;
}

function FolderNode({ folder, level, isSelected, onSelect }: FolderNodeProps) {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const hasChildren = folder.children && folder.children.length > 0;
  
  const getIcon = () => {
    if (folder.type === 'category') {
      return (
        <div 
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: folder.color || '#9e9e9e' }}
        />
      );
    }
    if (folder.type === 'label') {
      return <Tag className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />;
    }
    return <Folder className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />;
  };
  
  return (
    <div>
      <div
        className="flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer transition-colors"
        style={{
          paddingLeft: `${8 + level * 16}px`,
          backgroundColor: isSelected ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
          color: isSelected ? 'var(--brand-primary)' : 'var(--text-secondary)',
        }}
        onMouseEnter={e => {
          if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
        }}
        onMouseLeave={e => {
          if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {/* Expand/Collapse Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) setIsExpanded(!isExpanded);
          }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ 
            visibility: hasChildren ? 'visible' : 'hidden',
            color: 'var(--text-tertiary)',
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>
        
        {/* Folder Icon */}
        <span className="flex-shrink-0">{getIcon()}</span>
        
        {/* Name */}
        <span 
          className="flex-1 truncate text-sm"
          onClick={() => onSelect(folder)}
        >
          {folder.displayName}
        </span>
        
        {/* Unread Count */}
        {folder.unreadCount !== undefined && folder.unreadCount > 0 && (
          <span 
            className="text-xs px-1.5 py-0.5 rounded-full"
            style={{
              backgroundColor: 'var(--brand-primary)',
              color: 'white',
            }}
          >
            {folder.unreadCount}
          </span>
        )}
        
        {/* Add Button */}
        {!isSelected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect(folder);
            }}
            className="w-5 h-5 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-all"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      
      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children!.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              level={level + 1}
              isSelected={false} // We'll update this from parent
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FolderTree({ folders, selectedIds, onSelect }: FolderTreeProps) {
  // Separate folders, labels, and categories
  const mailFolders = folders.filter(f => f.type === 'folder');
  const labels = folders.filter(f => f.type === 'label');
  const categories = folders.filter(f => f.type === 'category');
  
  const isSelected = (id: string) => selectedIds.includes(id);
  
  const renderSection = (title: string, emoji: string, items: TreeFolder[]) => {
    if (items.length === 0) return null;
    
    return (
      <div className="mb-4">
        <h4 
          className="text-xs font-semibold uppercase tracking-wider mb-2 px-2 flex items-center gap-1.5"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <span>{emoji}</span>
          {title}
        </h4>
        <div className="space-y-0.5">
          {items.map((folder) => (
            <FolderNode
              key={folder.id}
              folder={folder}
              level={0}
              isSelected={isSelected(folder.id)}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div className="py-2">
      {renderSection('Ordner', '📁', mailFolders)}
      {renderSection('Kategorien', '🏷️', categories)}
      {renderSection('Labels', '🔖', labels)}
      
      {folders.length === 0 && (
        <p 
          className="text-sm text-center py-4"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Keine Ordner gefunden
        </p>
      )}
    </div>
  );
}
